import {
  Alert,
  Button,
  Modal,
  Radio,
  RadioGroup,
  Stack,
  Textarea,
} from "@assetwatch/ui";
import { useState } from "react";
import type { WorkRequestId } from "../../../lib/workIds";
import type { SelectableWorkRequestDeclineReasonCode } from "../../../types/workRequest";

const declineReasons: {
  code: SelectableWorkRequestDeclineReasonCode;
  label: string;
}[] = [
  { code: "ALREADY_SCHEDULED", label: "Already scheduled or planned" },
  { code: "NOT_MAINTENANCE", label: "Not a maintenance issue" },
  { code: "INSUFFICIENT_DETAIL", label: "Insufficient detail" },
  { code: "OTHER", label: "Other" },
];

export type DeclineRequestDialogInput = {
  reasonCode: SelectableWorkRequestDeclineReasonCode;
  note: string | null;
  duplicateOfWorkRequestId: WorkRequestId | null;
};

type DeclineRequestDialogProps = {
  error: string | null;
  isPending: boolean;
  onConfirm: (input: DeclineRequestDialogInput) => void;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  requestNumber: string;
};

export function DeclineRequestDialog({
  error,
  isPending,
  onConfirm,
  onOpenChange,
  open,
  requestNumber,
}: DeclineRequestDialogProps) {
  const [reasonCode, setReasonCode] =
    useState<SelectableWorkRequestDeclineReasonCode | null>(null);
  const [note, setNote] = useState("");
  const noteRequired = reasonCode === "OTHER";
  const noteMissing = noteRequired && note.trim().length === 0;
  const reasonMissing = reasonCode === null;

  function resetState() {
    setReasonCode(null);
    setNote("");
  }

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen && !isPending) {
      resetState();
    }
    onOpenChange(nextOpen);
  }

  function handleReasonChange(value: string) {
    if (!value) return;
    const nextReasonCode = value as SelectableWorkRequestDeclineReasonCode;
    setReasonCode(nextReasonCode);
  }

  function handleConfirm() {
    if (reasonMissing || noteMissing || reasonCode === null) return;
    onConfirm({
      reasonCode,
      note: note.trim() ? note.trim() : null,
      duplicateOfWorkRequestId: null,
    });
  }

  return (
    <Modal
      dismissible={!isPending}
      footer={
        <>
          <Button
            disabled={isPending}
            onClick={() => handleOpenChange(false)}
            variant="ghost"
          >
            Cancel
          </Button>
          <Button
            disabled={reasonMissing || noteMissing}
            intent="destructive"
            loading={isPending}
            loadingText="Declining request"
            onClick={handleConfirm}
          >
            Decline request
          </Button>
        </>
      }
      onOpenChange={handleOpenChange}
      open={open}
      title={`Decline ${requestNumber}`}
    >
      <Stack gap={16}>
        <RadioGroup
          label="REASON"
          onValueChange={handleReasonChange}
          value={reasonCode ?? ""}
        >
          {declineReasons.map((option) => (
            <Radio key={option.code} label={option.label} value={option.code} />
          ))}
        </RadioGroup>
        <Textarea
          aria-label="Note to requester"
          error={noteMissing ? "A note is required for Other." : undefined}
          label={
            noteRequired ? "NOTE TO REQUESTER (REQUIRED)" : "NOTE TO REQUESTER"
          }
          maxLength={500}
          onChange={(event) => setNote(event.target.value)}
          placeholder={
            noteRequired ? "Enter decline reason" : "Optional context"
          }
          value={note}
        />
        {error ? <Alert tone="negative" title={error} /> : null}
      </Stack>
    </Modal>
  );
}
